import { useState } from "react";
import type { TipoNodoAjuste } from "@/api/metas.service";
import { somarHistoricos, type Arvore, type NoMeta } from "./metasArvore";
import type { ItemContribuicao } from "./metasFormato";

/** Id sintético da raiz da visão Grupo — nunca colide com um id_nodo real, que vem do ERP. */
const ID_RAIZ_GRUPO = "GRUPO";

/**
 * Nó sintético da visão Grupo: `membros` são os nós reais (um por loja, em
 * geral) que caem no mesmo nome dentro do mesmo pai agrupado. A raiz agrupa
 * todas as lojas; descendo, cada nível agrupa os filhos já carregados desses
 * membros por nome — só o que já está em `arvore.filhos` entra na soma, daí
 * expandir um nó do Grupo carregar os filhos de todo membro que ainda não os
 * tem.
 */
export interface NoGrupo {
  id: string;
  nome: string;
  tipoNodo: TipoNodoAjuste;
  nivel: number;
  membros: string[];
}

/**
 * Consulta consolidada por nome de categoria entre todas as lojas do
 * cenário. O ERP não tem uma consulta que já some assim (`tb_flash_nodo` é
 * uma árvore por loja) — o Grupo é montado no cliente a partir do que já foi
 * carregado; editar um grupo vira um ajuste por membro (ver `aplicarEdicaoGrupo`).
 */
export function useVisaoGrupo(params: {
  lojas: string[];
  nos: Record<string, NoMeta>;
  filhosCarregados: Record<string, string[]>;
  ajustada: (id: string) => number;
  calculadaDe: (id: string) => number;
  contribuicaoDe: (id: string) => number;
  carregando: Set<string>;
  carregarFilhos: (id: string) => Promise<void>;
}) {
  const { lojas, nos, filhosCarregados, ajustada, calculadaDe, contribuicaoDe, carregando, carregarFilhos } = params;
  const [expandidosGrupo, setExpandidosGrupo] = useState<Set<string>>(new Set());

  const raizGrupo: NoGrupo = { id: ID_RAIZ_GRUPO, nome: "GRUPO", tipoNodo: "EMPRESA", nivel: 0, membros: lojas };

  const rotuloNivelGrupo = (grupo: NoGrupo) => {
    if (grupo.nivel === 0) return "GRUPO";
    if (grupo.tipoNodo === "PRODUTO") return "Produto";
    return `N${grupo.nivel}`;
  };

  const calculadaGrupo = (grupo: NoGrupo) => grupo.membros.reduce((acc, id) => acc + calculadaDe(id), 0);
  const ajustadaGrupo = (grupo: NoGrupo) => grupo.membros.reduce((acc, id) => acc + ajustada(id), 0);
  const contribuicaoGrupo = (grupo: NoGrupo) => grupo.membros.reduce((acc, id) => acc + contribuicaoDe(id), 0);
  const historicoGrupo = (grupo: NoGrupo) => somarHistoricos(grupo.membros, nos);
  // Um item por membro pra `percentualContribuicao`/`fracaoContribuicao`: cada
  // membro é um nó real, com seu próprio PERC_CONTRIBUICAO_ENVIADO — a %
  // agregada do grupo é uma média ponderada por venda calculada, não existe
  // "percentual de grupo" vindo do ERP.
  const itensContribuicaoGrupo = (grupo: NoGrupo): ItemContribuicao[] =>
    grupo.membros.map((id) => ({
      percEnviado: nos[id]?.percContribuicaoEnviado ?? null,
      contribuicao: contribuicaoDe(id),
      calculada: calculadaDe(id),
    }));

  /**
   * Filhos de `membros` (ids reais), já carregados em `filhosMap`, juntados
   * por nome — mas só entre nós do mesmo `tipoNodo`: duas lojas podem ter,
   * sob nomes iguais, uma categoria numa e um produto folha na outra, e
   * juntar os dois faria o grupo herdar o `tipoNodo` de qualquer um deles,
   * podendo esconder os filhos reais do outro tipo (ex.: virar "sem filhos"
   * só porque o produto venceu). A chave carrega o tipo pra não colidir.
   */
  const agruparPorNome = (
    membros: string[],
    filhosMap: Record<string, string[]>,
    nosMap: Record<string, NoMeta>,
  ) => {
    const porChave = new Map<string, { nome: string; tipoNodo: TipoNodoAjuste; membros: string[] }>();
    membros.forEach((membroId) => {
      (filhosMap[membroId] ?? []).forEach((filhoId) => {
        const filho = nosMap[filhoId];
        if (!filho) return;
        const chave = `${filho.tipoNodo}:${filho.nome}`;
        const existente = porChave.get(chave);
        if (existente) existente.membros.push(filhoId);
        else porChave.set(chave, { nome: filho.nome, tipoNodo: filho.tipoNodo, membros: [filhoId] });
      });
    });
    return porChave;
  };

  /** Filhos dos membros de `grupo`, já carregados, juntados por nome (e tipo). */
  const filhosDeGrupo = (grupo: NoGrupo): NoGrupo[] =>
    [...agruparPorNome(grupo.membros, filhosCarregados, nos).entries()]
      .sort(([, a], [, b]) => a.nome.localeCompare(b.nome))
      .map(([chave, { nome, tipoNodo, membros }]) => ({
        id: `${grupo.id}/${chave}`,
        nome,
        tipoNodo,
        nivel: grupo.nivel + 1,
        membros,
      }));

  const carregandoGrupo = (grupo: NoGrupo) => grupo.membros.some((id) => carregando.has(id));

  const alternarExpandidoGrupo = (grupo: NoGrupo) => {
    const abrindo = !expandidosGrupo.has(grupo.id);
    setExpandidosGrupo((prev) => {
      const proximo = new Set(prev);
      if (!proximo.delete(grupo.id)) proximo.add(grupo.id);
      return proximo;
    });
    if (abrindo) grupo.membros.forEach((id) => void carregarFilhos(id));
  };

  const abrirRaiz = () => setExpandidosGrupo(new Set([ID_RAIZ_GRUPO]));
  const recolher = () => setExpandidosGrupo(new Set());

  /**
   * Usada por "Exibir até": expande a visão Grupo até `nivelAlvo`, a partir
   * de uma árvore que o chamador já esperou carregar até lá. Recebida por
   * parâmetro (em vez de usar `filhosCarregados`/`nos` do hook) porque essas
   * props só chegam atualizadas no próximo render — tarde demais pra expandir
   * na hora com os níveis que acabaram de ser buscados.
   *
   * Abre até `nivelAlvo` inclusive — mesmo critério de `useArvoreMetas.
   * trocarProfundidade` (`nivelRelativo <= nivel`, que equivale a
   * `no.nivel < nivel` já que `nivelRelativo` conta a partir de 1): os nós do
   * próprio nível pedido ficam com as linhas visíveis, mas não abertos — só
   * os ancestrais deles é que abrem, senão o nível pedido revelaria o de
   * baixo também (N1 revelando N2 ao "Exibir até N1").
   */
  const expandirAteNivel = (nivelAlvo: number, arvore: Arvore | null) => {
    if (nivelAlvo <= 0 || !arvore) {
      setExpandidosGrupo(new Set());
      return;
    }
    const abertos = new Set<string>([ID_RAIZ_GRUPO]);
    let nivelAtual: { id: string; membros: string[] }[] = [{ id: ID_RAIZ_GRUPO, membros: lojas }];
    for (let n = 1; n <= nivelAlvo; n++) {
      const proximoNivel: { id: string; membros: string[] }[] = [];
      nivelAtual.forEach(({ id: paiId, membros }) => {
        agruparPorNome(membros, arvore.filhos, arvore.nos).forEach((info, chave) => {
          proximoNivel.push({ id: `${paiId}/${chave}`, membros: info.membros });
        });
      });
      if (n < nivelAlvo) proximoNivel.forEach((g) => abertos.add(g.id));
      nivelAtual = proximoNivel;
    }
    setExpandidosGrupo(abertos);
  };

  return {
    raizGrupo,
    expandidosGrupo,
    rotuloNivelGrupo,
    calculadaGrupo,
    ajustadaGrupo,
    contribuicaoGrupo,
    itensContribuicaoGrupo,
    historicoGrupo,
    filhosDeGrupo,
    carregandoGrupo,
    alternarExpandidoGrupo,
    abrirRaiz,
    recolher,
    expandirAteNivel,
  };
}
